'use client';

import React, { useState } from "react";
import { X, Plus, ArrowUp, ArrowDown, Lock } from "lucide-react";
import { useJigyasa } from "./state";
import { Sheet } from "./sheets";

const slug = (s) => s.replace(/\s+/g, "-").toLowerCase();

// Lets the owner choose which pipeline (line of business) they're editing,
// right inside the editor — so they can view/edit each pipeline's stages,
// types and lost reasons without leaving Settings. Hidden for single-line orgs.
const PipelinePicker = () => {
  const { lines, activeLine, setActiveLine } = useJigyasa();
  if (!lines || lines.length <= 1) return null;
  return (
    <div className="mb-3">
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Pipeline</label>
      <select
        data-testid="settings-line-picker"
        value={activeLine.id}
        onChange={(e) => setActiveLine(e.target.value)}
        className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm font-medium"
      >
        {lines.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </div>
  );
};

// Generic editable-list drawer. Each organisation manages its own simple
// lists (sources, enquiry types, lost reasons). Lists are pre-filled from the
// business-type template, so the operator never faces a blank screen.
//
// "Archive, don't delete": removing an item only takes it out of the dropdowns
// for NEW records — existing enquiries keep the label that was stored on them
// (the value lives on each enquiry, not as a reference), so history and reports
// stay intact. The last remaining item can't be removed so a list is never empty.
//
// Owner/admin only: when `canEdit` is false the add box and remove buttons are
// hidden and the list is read-only.
const ListEditor = ({
  open,
  onClose,
  testId,
  title,
  note,
  subNote = null,
  feedback = null,
  items,
  onUpdate,
  ids,
  placeholder,
  canEdit = true,
  picker = null,
}) => {
  const [newItem, setNewItem] = useState("");
  const [flash, setFlash] = useState(null);
  const list = items || [];

  // Instant, human feedback (idiot-proof rule 4) — only for editors that
  // opt in via `feedback`, so sister screens stay unchanged for now.
  const flashMsg = (msg) => {
    if (!feedback) return;
    setFlash(msg);
    setTimeout(() => setFlash((cur) => (cur === msg ? null : cur)), 2500);
  };

  const add = () => {
    const v = newItem.trim();
    if (!v || list.some((s) => s.toLowerCase() === v.toLowerCase())) return;
    onUpdate([...list, v]);
    setNewItem("");
    flashMsg(feedback?.added || "Added ✓");
  };

  const remove = (s) => {
    if (list.length <= 1) return;
    onUpdate(list.filter((x) => x !== s));
    flashMsg(feedback?.removed || "Removed ✓");
  };

  return (
    <Sheet open={open} onClose={onClose} testId={testId} title={title}>
      {picker}
      <div className="mb-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
        {note}
      </div>
      <ul className="space-y-2">
        {list.map((s) => (
          <li
            key={s}
            data-testid={ids.item(s)}
            className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-800"
          >
            <span>{s}</span>
            {canEdit && (
              <button
                data-testid={ids.remove(s)}
                onClick={() => remove(s)}
                disabled={list.length <= 1}
                className="rounded-full p-1 text-zinc-400 hover:bg-zinc-100 hover:text-red-600 disabled:opacity-30"
                aria-label={`Remove ${s}`}
              >
                <X size={16} />
              </button>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="mt-3 flex gap-2">
          <input
            data-testid={ids.input}
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder={placeholder}
            className="flex-1 rounded-lg border border-zinc-300 px-3 py-2.5 text-sm"
          />
          <button
            data-testid={ids.add}
            onClick={add}
            disabled={!newItem.trim()}
            className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            <Plus size={16} /> Add
          </button>
        </div>
      )}
      {flash && (
        <p data-testid={`${testId}-flash`} className="mt-2 text-xs font-medium text-emerald-700">
          {flash}
        </p>
      )}
      {subNote && (
        <p data-testid={`${testId}-subnote`} className="mt-3 text-xs text-zinc-400">
          {subNote}
        </p>
      )}
    </Sheet>
  );
};

// --- Sources (existing testids preserved) ---
export const ManageSourcesSheet = ({ open, onClose, canEdit = true }) => {
  const { orgSettings, updateSources } = useJigyasa();
  const live = orgSettings.sources?.length ? orgSettings.sources : ["Walk-in"];
  const example = live[0];
  return (
    <ListEditor
      open={open}
      onClose={onClose}
      testId="manage-sources-sheet"
      title="Where do people find you?"
      note={`When someone contacts you, where did they come from? You pick one while saving the enquiry — over time it shows which of your marketing actually brings customers. Example: someone found you through “${example}” — that's what you pick.`}
      subNote="Removing one only hides it from new enquiries — your old records keep it."
      feedback={{ added: "Added ✓ — it now shows when you save an enquiry.", removed: "Removed — old enquiries keep it." }}
      items={orgSettings.sources}
      onUpdate={updateSources}
      canEdit={canEdit}
      placeholder={`Add one — e.g. ${live[live.length - 1]}`}
      ids={{
        item: (s) => `source-item-${slug(s)}`,
        remove: (s) => `remove-source-${slug(s)}`,
        input: "new-source-input",
        add: "add-source-btn",
      }}
    />
  );
};

// --- Enquiry types ---
export const ManageTypesSheet = ({ open, onClose, canEdit = true }) => {
  const { enquiryTypes, updateEnquiryTypes, lines, activeLine, costume } = useJigyasa();
  const scope = lines.length > 1 ? `For the “${activeLine.name}” line. ` : "";
  const live = enquiryTypes?.length ? enquiryTypes : costume.enquiryTypes;
  const example = live[0] || "Pricing";
  return (
    <ListEditor
      open={open}
      onClose={onClose}
      testId="manage-types-sheet"
      title="What do people ask you for?"
      note={`${scope}When someone contacts you, what do they want? You pick one of these while saving the enquiry — so every card shows at a glance what the person is after. Example: someone asks about ${example.toLowerCase()} — you pick “${example}”.`}
      subNote="Removing one only hides it from new enquiries — your old records keep their label."
      feedback={{ added: "Added ✓ — it now shows when you save an enquiry.", removed: "Removed — old enquiries keep it." }}
      items={enquiryTypes}
      onUpdate={updateEnquiryTypes}
      picker={<PipelinePicker />}
      canEdit={canEdit}
      placeholder={`Add one — e.g. ${live[live.length - 1] || "Site visit"}`}
      ids={{
        item: (s) => `type-item-${slug(s)}`,
        remove: (s) => `remove-type-${slug(s)}`,
        input: "new-type-input",
        add: "add-type-btn",
      }}
    />
  );
};

// --- Lost reasons ---
export const ManageLostReasonsSheet = ({ open, onClose, canEdit = true }) => {
  const { lostReasons, updateLostReasons, lines, activeLine } = useJigyasa();
  const scope = lines.length > 1 ? `For the “${activeLine.name}” line. ` : "";
  const live = lostReasons?.length ? lostReasons : ["Chose a competitor"];
  return (
    <ListEditor
      open={open}
      onClose={onClose}
      testId="manage-lost-reasons-sheet"
      title="Reasons a lead is lost"
      note={`${scope}When you mark a lead lost, you pick one of these reasons. Over time this shows you WHY you lose deals — price, competitor, timing — so you know exactly what to fix.`}
      subNote="Removing one only hides it from new lost leads — your old records keep their reason."
      feedback={{ added: "Added ✓ — it now shows when you mark a lead lost.", removed: "Removed — old records keep their reason." }}
      items={lostReasons}
      onUpdate={updateLostReasons}
      picker={<PipelinePicker />}
      canEdit={canEdit}
      placeholder={`Add one — e.g. ${live[live.length - 1]}`}
      ids={{
        item: (s) => `lost-reason-item-${slug(s)}`,
        remove: (s) => `remove-lost-reason-${slug(s)}`,
        input: "new-lost-reason-input",
        add: "add-lost-reason-btn",
      }}
    />
  );
};

// --- Statuses / pipeline stages (Step 3) ---
// The riskiest list: stages drive win/loss, auto-customer-creation and the
// kanban. So three keys are PROTECTED system anchors — labels can be renamed
// but the key, presence and position are fixed:
//   "new"        — the entry stage every new lead lands in (pinned first)
//   "signed_up"  — the "won" stage that converts a lead (pinned near the end)
//   "lost"       — the "lost" stage (pinned last)
// Everything in between can be renamed, reordered, added and removed.
// A stage can only be removed when no leads are sitting in it (move them first)
// so history never breaks.
const STAGE_FIRST = "new";
const STAGE_ANCHORS_END = ["signed_up", "lost"];
const isAnchorStage = (key) => key === STAGE_FIRST || STAGE_ANCHORS_END.includes(key);

export const ManageStatusesSheet = ({ open, onClose, canEdit = true }) => {
  const { stages, updateStages, enquiries, lines, activeLine } = useJigyasa();
  const [draft, setDraft] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const scope = lines.length > 1 ? `For the “${activeLine.name}” line. ` : "";

  React.useEffect(() => {
    if (open) {
      setDraft(stages.map((s) => ({ ...s })));
      setNewLabel("");
      setError("");
    }
  }, [open, stages]);
  // Reset the "Saved ✓" state only when the sheet (re)opens — saving itself
  // updates `stages`, and resetting on that change would wipe the flash.
  React.useEffect(() => {
    if (open) setSaved(false);
  }, [open]);

  const counts = React.useMemo(() => {
    const c = {};
    (enquiries || []).forEach((e) => {
      if ((e.line_id || "default") === activeLine.id) c[e.stage] = (c[e.stage] || 0) + 1;
    });
    return c;
  }, [enquiries, activeLine.id]);

  const setLabel = (i, label) =>
    setDraft((d) => d.map((s, idx) => (idx === i ? { ...s, label } : s)));

  const canMove = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= draft.length) return false;
    return !isAnchorStage(draft[i].key) && !isAnchorStage(draft[j].key);
  };

  const move = (i, dir) => {
    if (!canMove(i, dir)) return;
    const j = i + dir;
    setDraft((d) => {
      const copy = [...d];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const removeStage = (i) => {
    const key = draft[i].key;
    if (isAnchorStage(key) || (counts[key] || 0) > 0) return;
    setDraft((d) => d.filter((_, idx) => idx !== i));
    setError("");
  };

  const addStage = () => {
    const label = newLabel.trim();
    if (!label) return;
    if (draft.some((s) => s.label.trim().toLowerCase() === label.toLowerCase())) {
      setError("You already have a stage with that name.");
      return;
    }
    const key = `custom_${Date.now().toString(36)}`;
    const insertAt = draft.findIndex((s) => STAGE_ANCHORS_END.includes(s.key));
    setDraft((d) => {
      const copy = [...d];
      copy.splice(insertAt < 0 ? copy.length : insertAt, 0, { key, label });
      return copy;
    });
    setNewLabel("");
    setError("");
  };

  const save = () => {
    if (draft.some((s) => !s.label.trim())) {
      setError("Every stage needs a name.");
      return;
    }
    const keys = draft.map((s) => s.key);
    if (!keys.includes("new") || !keys.includes("signed_up") || !keys.includes("lost")) {
      setError("The first, Won and Lost stages can't be removed.");
      return;
    }
    updateStages(draft.map((s) => ({ key: s.key, label: s.label.trim() })));
    setSaved(true);
    setTimeout(onClose, 900);
  };

  return (
    <Sheet open={open} onClose={onClose} testId="manage-statuses-sheet" title="Follow-up steps">
      <PipelinePicker />
      <div className="mb-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
        {scope}These are the columns on your board — the steps every enquiry moves through, from
        first contact to Won. Rename a step by typing in it, and use the arrows to reorder.
        <span className="mt-1 block text-zinc-400">
          The first step, <strong>Won</strong> and <strong>Lost</strong> are fixed. A step with
          leads in it can't be removed — move those leads first.
        </span>
      </div>

      <ul className="space-y-2">
        {draft.map((s, i) => {
          const anchor = isAnchorStage(s.key);
          const count = counts[s.key] || 0;
          return (
            <li
              key={s.key}
              data-testid={`status-row-${s.key}`}
              className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-2 py-2"
            >
              {canEdit && (
                <div className="flex flex-col">
                  <button
                    data-testid={`status-up-${s.key}`}
                    onClick={() => move(i, -1)}
                    disabled={!canMove(i, -1)}
                    className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-20"
                    aria-label="Move up"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    data-testid={`status-down-${s.key}`}
                    onClick={() => move(i, 1)}
                    disabled={!canMove(i, 1)}
                    className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-20"
                    aria-label="Move down"
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>
              )}

              {canEdit ? (
                <input
                  data-testid={`status-label-${s.key}`}
                  value={s.label}
                  onChange={(e) => setLabel(i, e.target.value)}
                  className="min-w-0 flex-1 rounded-md border border-zinc-300 px-2.5 py-2 text-sm"
                />
              ) : (
                <span className="min-w-0 flex-1 text-sm text-zinc-800">{s.label}</span>
              )}

              {count > 0 && (
                <span className="shrink-0 text-[11px] text-zinc-400">{count} lead{count === 1 ? "" : "s"}</span>
              )}

              {anchor ? (
                <span
                  data-testid={`status-fixed-${s.key}`}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500"
                >
                  <Lock size={11} /> Fixed
                </span>
              ) : (
                canEdit && (
                  <button
                    data-testid={`status-remove-${s.key}`}
                    onClick={() => removeStage(i)}
                    disabled={count > 0}
                    title={count > 0 ? "Move these leads to another stage first" : "Remove stage"}
                    className="shrink-0 rounded-full p-1 text-zinc-400 hover:bg-zinc-100 hover:text-red-600 disabled:opacity-30"
                    aria-label={`Remove ${s.label}`}
                  >
                    <X size={16} />
                  </button>
                )
              )}
            </li>
          );
        })}
      </ul>

      {error && (
        <p data-testid="statuses-error" className="mt-2 text-xs text-red-600">{error}</p>
      )}

      {canEdit && (
        <>
          <div className="mt-3 flex gap-2">
            <input
              data-testid="new-status-input"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addStage()}
              placeholder="Add a stage — e.g. Negotiating"
              className="flex-1 rounded-lg border border-zinc-300 px-3 py-2.5 text-sm"
            />
            <button
              data-testid="add-status-btn"
              onClick={addStage}
              disabled={!newLabel.trim()}
              className="inline-flex items-center gap-1 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
            >
              <Plus size={16} /> Add
            </button>
          </div>
          <button
            data-testid="save-statuses-btn"
            onClick={save}
            disabled={saved}
            className={`mt-3 w-full rounded-lg px-3 py-2.5 text-sm font-semibold text-white ${
              saved ? "bg-emerald-600" : "bg-rose-600 hover:bg-rose-700"
            }`}
          >
            {saved ? "Saved ✓" : "Save my steps"}
          </button>
        </>
      )}
    </Sheet>
  );
};

// --- Lines of business (multiple pipelines per org) ---
// An organisation can run more than one sales journey (e.g. Software AND
// Mutual funds). Each "line" has its own statuses, types and lost reasons.
// Pick a line here to make it the active one, then edit its lists from the
// Statuses / Types / Lost reasons menus. A line can only be removed when it
// has no leads in it, and you must always keep at least one.
export const ManageLinesSheet = ({ open, onClose, canEdit = true }) => {
  const { lines, activeLine, setActiveLine, addLine, renameLine, removeLine, enquiries } = useJigyasa();
  const [newName, setNewName] = useState("");

  const counts = React.useMemo(() => {
    const c = {};
    (enquiries || []).forEach((e) => { const k = e.line_id || "default"; c[k] = (c[k] || 0) + 1; });
    return c;
  }, [enquiries]);

  const add = () => {
    const v = newName.trim();
    if (!v) return;
    if (lines.some((l) => l.name.trim().toLowerCase() === v.toLowerCase())) return;
    addLine(v);
    setNewName("");
  };

  return (
    <Sheet open={open} onClose={onClose} testId="manage-lines-sheet" title="Lines of business">
      <div className="mb-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
        Most businesses have one. Add a second only if you run a truly different journey (for
        example, Software and Mutual funds). Each line has its own statuses, types and lost
        reasons. Tap a line to work on it, then edit its lists from the Statuses / Types /
        Lost reasons menus.
      </div>

      <ul className="space-y-2">
        {lines.map((l) => {
          const count = counts[l.id] || 0;
          const isActive = activeLine.id === l.id;
          return (
            <li
              key={l.id}
              data-testid={`line-row-${l.id}`}
              className={`flex items-center gap-2 rounded-lg border px-2 py-2 ${
                isActive ? "border-rose-300 bg-rose-50" : "border-zinc-200 bg-white"
              }`}
            >
              <button
                data-testid={`line-select-${l.id}`}
                onClick={() => setActiveLine(l.id)}
                title="Work on this line"
                className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  isActive ? "border-rose-400 bg-rose-600 text-white" : "border-zinc-300 text-zinc-500 hover:bg-zinc-100"
                }`}
              >
                {isActive ? "Active" : "Use"}
              </button>

              {canEdit ? (
                <input
                  data-testid={`line-name-${l.id}`}
                  value={l.name}
                  onChange={(e) => renameLine(l.id, e.target.value)}
                  className="min-w-0 flex-1 rounded-md border border-zinc-300 px-2.5 py-2 text-sm"
                />
              ) : (
                <span className="min-w-0 flex-1 text-sm text-zinc-800">{l.name}</span>
              )}

              {count > 0 && (
                <span className="shrink-0 text-[11px] text-zinc-400">{count} lead{count === 1 ? "" : "s"}</span>
              )}

              {canEdit && (
                <button
                  data-testid={`line-remove-${l.id}`}
                  onClick={() => removeLine(l.id)}
                  disabled={lines.length <= 1 || count > 0}
                  title={count > 0 ? "Move these leads to another line first" : lines.length <= 1 ? "Keep at least one line" : "Remove line"}
                  className="shrink-0 rounded-full p-1 text-zinc-400 hover:bg-zinc-100 hover:text-red-600 disabled:opacity-30"
                  aria-label={`Remove ${l.name}`}
                >
                  <X size={16} />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {canEdit && (
        <div className="mt-3 flex gap-2">
          <input
            data-testid="new-line-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Add a line — e.g. Mutual funds"
            className="flex-1 rounded-lg border border-zinc-300 px-3 py-2.5 text-sm"
          />
          <button
            data-testid="add-line-btn"
            onClick={add}
            disabled={!newName.trim()}
            className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            <Plus size={16} /> Add
          </button>
        </div>
      )}
    </Sheet>
  );
};
